import { useEffect, useState } from "react"
import { Card, Empty, message, Result, Tabs } from "antd"
import TableBox from "../../../components/TableBox/TableBox"
import apiClient from "../../../Utils/apiClient"
import { isUnixDevice, osUpdateLabel } from "../../../Utils/deviceOs"

const CheckForUpdates = ({ activeKey, device, handleClick }) => {
  const [windows, setWindows] = useState([])
  const [applications, setApplications] = useState([])
  const [checking, setChecking] = useState(false)
  const [checked, setChecked] = useState(false)
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
  })

  const parseOutput = (output) => {
    if (!output) return { windows: [], applications: [] }
    try {
      const parsed = JSON.parse(output)
      if (Array.isArray(parsed)) {
        return { windows: parsed, applications: [] }
      }
      const win = parsed.windows || parsed.Windows || []
      const apps = parsed.applications || parsed.applicationUpdates || []
      return {
        windows: Array.isArray(win) ? win : [],
        applications: Array.isArray(apps) ? apps : [],
      }
    } catch (err) {
      return { windows: [], applications: [] }
    }
  }

  const fetchData = async () => {
    if (!device?.hostname || isUnixDevice(device)) return
    setChecking(true)
    setChecked(false)
    const payload = {
      entityType: "checkforupdates",
      hostname: device.hostname,
      name: "checkforupdates",
      command: "check-updates",
    }

    try {
      const response = await apiClient(
        `devices/${device.hostname}/task`,
        "POST",
        payload
      )
      message.success(`Created Task For ${device.hostname}`)
      const data = response?.data || response
      if (data?.success === false || data?.status === "Pending") {
        message.warning(
          data?.message ||
            "The update scan is still running. Open Jobs to watch it, then scan again."
        )
        return
      }
      if (!data?.output) {
        message.warning("The scan finished without a result list. Try Scan apps from the device page.")
        return
      }
      const next = parseOutput(data.output)
      setWindows(next.windows)
      setApplications(next.applications)
      setChecked(true)
    } catch (err) {
      console.error("Failed to fetch updates", err)
      message.error("Failed to create update check task.")
    } finally {
      setChecking(false)
    }
  }

  useEffect(() => {
    if (activeKey === "checkforupdates" && handleClick) {
      fetchData()
    }
  }, [handleClick])

  const windowsColumns = [
    { title: "Title", dataIndex: "Title", key: "Title" },
    { title: "KB", dataIndex: "KB", key: "KB" },
  ]
  const appColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Id", dataIndex: "appName", key: "appName" },
    { title: "Current", dataIndex: "currentVersion", key: "currentVersion" },
    {
      title: "Available",
      dataIndex: "availableVersion",
      key: "availableVersion",
    },
    { title: "Source", dataIndex: "source", key: "source" },
  ]

  if (isUnixDevice(device)) {
    return (
      <Card>
        <Result
          status="info"
          title={osUpdateLabel(device)}
          subTitle="Use Update OS on the Patches tab. Windows Update scans do not run on this device."
        />
      </Card>
    )
  }

  if (checking) {
    return (
      <Card>
        <Empty description="Checking for Windows and app updates..." />
      </Card>
    )
  }

  if (checked && !windows.length && !applications.length) {
    return (
      <Card>
        <Result status="success" title="Windows and app updates are up to date" />
      </Card>
    )
  }

  return (
    <Tabs
      items={[
        {
          key: "windows",
          label: `Windows (${windows.length})`,
          children: windows.length ? (
            <TableBox
              data={windows}
              columns={windowsColumns}
              loading={false}
              pagination={{
                total: windows.length,
                current: paginationData.page,
                pageSize: paginationData.pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                onChange: (page, pageSize) => {
                  setPaginationData((prev) => ({ ...prev, page, pageSize }))
                },
              }}
            />
          ) : (
            <Card>
              <Result status="success" title="Windows updates are up to date" />
            </Card>
          ),
        },
        {
          key: "apps",
          label: `Apps (${applications.length})`,
          children: applications.length ? (
            <TableBox data={applications} columns={appColumns} loading={false} />
          ) : (
            <Card>
              <Result status="success" title="App updates are up to date" />
            </Card>
          ),
        },
      ]}
    />
  )
}

export default CheckForUpdates
